// ⚖️ AI_141 — Abstinência alcoólica (etiologia alcoólica).
// ─────────────────────────────────────────────────────────────
// O médico registra a DATA DA ÚLTIMA INGESTA; o sistema calcula o tempo de
// abstinência. O critério VIGENTE de listagem para transplante é de 6 meses.
// A nova portaria reduzirá esse prazo para 3 meses.
//
// ⟳ REVISAR quando a nova portaria entrar em vigor: trocar SOMENTE a constante
// MESES_ABSTINENCIA_EXIGIDOS abaixo (6 → 3). Fonte única do umbral e do cálculo,
// consumida pela aba Avaliação Clínica (UI) e pelo formatador da IA.
// ─────────────────────────────────────────────────────────────
import { hojeISO } from "./dataNaoFutura";

export const MESES_ABSTINENCIA_EXIGIDOS = 6;

export interface Abstinencia {
  meses: number; // meses completos de abstinência
  dias: number; // dias restantes além dos meses completos
  texto: string; // legível: "1 ano e 2 meses", "5 meses e 3 dias"
  atingeCriterio: boolean; // meses >= MESES_ABSTINENCIA_EXIGIDOS
}

/**
 * Calcula o tempo de abstinência entre a última ingesta e hoje (fuso de
 * Brasília, via hojeISO). `null` se a data faltar/for inválida ou for FUTURA
 * (não existe abstinência negativa). `hoje` é injetável para testes.
 */
export function calcularAbstinencia(
  dataUltimaIngesta?: string | null,
  hoje: string = hojeISO()
): Abstinencia | null {
  if (!dataUltimaIngesta) return null;
  const mi = String(dataUltimaIngesta).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  const mh = String(hoje).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!mi || !mh) return null;

  // Zero-padded p/ comparar cronologicamente por texto (última ingesta > hoje = futura).
  const iniISO = `${mi[1]}-${mi[2].padStart(2, "0")}-${mi[3].padStart(2, "0")}`;
  const fimISO = `${mh[1]}-${mh[2].padStart(2, "0")}-${mh[3].padStart(2, "0")}`;
  if (iniISO > fimISO) return null;

  const ini = { a: +mi[1], m: +mi[2], d: +mi[3] };
  const fim = { a: +mh[1], m: +mh[2], d: +mh[3] };

  let meses = (fim.a - ini.a) * 12 + (fim.m - ini.m);
  let dias = fim.d - ini.d;
  if (dias < 0) {
    // Empresta os dias do mês anterior ao mês de `fim`.
    meses -= 1;
    const diasMesAnterior = new Date(fim.a, fim.m - 1, 0).getDate();
    dias += diasMesAnterior;
  }

  const anos = Math.floor(meses / 12);
  const mesesResto = meses % 12;
  const partes: string[] = [];
  if (anos) partes.push(`${anos} ano${anos > 1 ? "s" : ""}`);
  if (mesesResto) partes.push(`${mesesResto} ${mesesResto > 1 ? "meses" : "mês"}`);
  if (!anos && dias) partes.push(`${dias} dia${dias > 1 ? "s" : ""}`);
  const texto = partes.length > 0 ? partes.join(" e ") : "menos de 1 dia";

  return {
    meses,
    dias,
    texto,
    atingeCriterio: meses >= MESES_ABSTINENCIA_EXIGIDOS,
  };
}
